import { motion } from "framer-motion";
import {
    AlertTriangle,
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    ClipboardCheck,
    FileDown,
    Lightbulb,
    ListChecks,
    Loader2,
    Lock,
    MapPin,
    MessageCircle,
    Phone,
    PhoneOff,
    Save,
    Sparkles,
    UserX,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import CallActionModal from "../components/scorecard/CallActionModal";
import ConfirmModal from "../components/common/ConfirmModal";
import EntretienItem from "../components/scorecard/EntretienItem";
import ScheduleImmersionModal from "../components/candidates/ScheduleImmersionModal";
import ScoreRing from "../components/candidates/ScoreRing";
import TalentPoolModal from "../components/candidates/TalentPoolModal";
import RejectReasonModal from "../components/candidates/RejectReasonModal";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { getQueuePosition, removeFromQueue } from "../lib/callQueue";
import { ENTRETIEN_SCRIPT, PHASES } from "../lib/entretienScript";
import { toE164 } from "../lib/format";
import { goBack } from "../lib/navigation";
import { hasRole } from "../lib/roles";
import SendMessageModal from "../components/messaging/SendMessageModal";

const TYPE_META = {
    prerec: {
        title: "Scorecard Pré-recrutement",
        subtitle: "Appel téléphonique - décision rapide avant immersion.",
        accent: "Pré-rec",
    },
    immersion: {
        title: "Scorecard Immersion Terrain",
        subtitle: "Observation réelle terrain - évaluation finale.",
        accent: "Immersion",
    },
};

function NoteSelector({ value, onChange }) {
    return (
        <div className="flex items-center gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => {
                const active = value === n;
                const isLow = n <= 2;
                return (
                    <button
                        key={n}
                        type="button"
                        data-testid={`note-${n}`}
                        onClick={() => onChange(active ? undefined : n)}
                        className={`h-9 w-9 rounded-xl text-sm font-extrabold transition active:scale-95 border-2 ${
                            active
                                ? isLow
                                    ? "bg-shariti-red text-white border-shariti-red shadow-sm"
                                    : n === 3
                                    ? "bg-shariti-yellow text-shariti-black border-shariti-yellow"
                                    : "bg-shariti-green text-white border-shariti-green shadow-sm"
                                : "bg-white text-slate-500 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                        }`}
                    >
                        {n}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * Build the ratings array sent to the backend / live-preview from the
 * unified entretien answers.
 *
 * Each script item carrying `criterion_ids` whose `note` is set produces
 * one rating per criterion_id (so a single Q can cover several criteria —
 * e.g. self-presentation covers énergie vocale + fluidité orale).
 */
function buildPrerecRatings(answers, items) {
    const ratings = [];
    for (const item of items) {
        const a = answers[item.id];
        if (!a?.note || !item.criterion_ids?.length) continue;
        for (const cid of item.criterion_ids) {
            ratings.push({
                criterion_id: cid,
                note: a.note,
                comment: a.comment || null,
            });
        }
    }
    return ratings;
}

export default function Scorecard() {
    const { id, type } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const meta = TYPE_META[type] || TYPE_META.prerec;
    const isPrerec = type === "prerec";
    const isReferent = hasRole(user, "referent");
    const isPreRecruteur = hasRole(user, "pre_recruteur");
    const accessDenied =
        (isPrerec && isReferent) || (!isPrerec && isPreRecruteur);

    const [criteria, setCriteria] = useState([]); // only for immersion
    const [ratings, setRatings] = useState({}); // criterion_id -> { note, comment } — immersion only
    const [scriptItems, setScriptItems] = useState(ENTRETIEN_SCRIPT); // prerec — loaded from API with static fallback
    const [entretienAnswers, setEntretienAnswers] = useState({}); // prerec — id -> { answer, note, comment }
    const [generalComment, setGeneralComment] = useState("");
    const [candidate, setCandidate] = useState(null);
    const [preview, setPreview] = useState({ total_score: 0, max_score: 0, percentage: 0, decision: "—", next_step: null, red_flags: [] });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitResult, setSubmitResult] = useState(null);
    const [validatingHiring, setValidatingHiring] = useState(false);
    const [markingNoAnswer, setMarkingNoAnswer] = useState(false);
    const [callModalOpen, setCallModalOpen] = useState(false);
    const [talentPoolModalOpen, setTalentPoolModalOpen] = useState(false);
    const [showImmersionModal, setShowImmersionModal] = useState(false);
    // iter97 · Force reject reason picker (only for the generic "Refuser"
    // button — the 3 explicit reason handlers already pass their reason).
    const [rejectReasonModal, setRejectReasonModal] = useState(null); // { defaultReason } | null
    // Confirmation modal shown when the user clicks "Enregistrer" while
    // some criteria/questions are still un-rated. Replaces window.confirm.
    const [incompleteConfirm, setIncompleteConfirm] = useState(null); // { answered, total, payload }
    // Messaging (SMS / WhatsApp)
    const [messagingState, setMessagingState] = useState(null);

    // Queue navigation — derived from localStorage saved by Candidates.jsx
    const queueInfo = useMemo(() => getQueuePosition(id), [id]);

    const loadCriteria = useCallback(async () => {
        const { data } = await api.get("/candidates/criteria");
        setCriteria(isPrerec ? data.prerec : data.immersion);
    }, [isPrerec]);

    const loadScript = useCallback(async () => {
        if (!isPrerec) return;
        try {
            const { data } = await api.get("/entretien-script", { params: { candidate_id: id } });
            if (Array.isArray(data) && data.length > 0) setScriptItems(data);
        } catch {
            // keep fallback ENTRETIEN_SCRIPT
        }
    }, [isPrerec, id]);

    const loadCandidate = useCallback(async () => {
        const { data } = await api.get(`/candidates/${id}`);
        setCandidate(data.candidate);
    }, [id]);

    /**
     * Load the most recent existing scorecard of the same type for this
     * candidate and pre-fill the form. So when the recruiter re-opens the
     * scorecard (e.g. after refusing the candidate then changing their mind),
     * they see their previous notes/answers instead of an empty form.
     */
    const loadExistingScorecard = useCallback(async () => {
        try {
            const { data } = await api.get(`/scorecards/candidate/${id}`);
            const latest = (Array.isArray(data) ? data : [])
                .filter((sc) => sc.type === type)
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
            if (!latest) return;

            // Pre-fill general comment
            if (latest.general_comment) setGeneralComment(latest.general_comment);

            // Pre-fill prerec answers from intake_answers (id -> { answer, note, comment })
            if (isPrerec && latest.intake_answers && typeof latest.intake_answers === "object") {
                setEntretienAnswers(latest.intake_answers);
            }

            // Pre-fill immersion ratings from ratings[] (criterion_id -> { note, comment })
            if (!isPrerec && Array.isArray(latest.ratings)) {
                const map = {};
                for (const r of latest.ratings) {
                    if (r.criterion_id) {
                        map[r.criterion_id] = { note: r.note, comment: r.comment || "" };
                    }
                }
                setRatings(map);
            }

            // Pre-fill the preview banner with the stored decision/scores
            setPreview((prev) => ({
                ...prev,
                total_score: latest.total_score ?? prev.total_score,
                max_score: latest.max_score ?? prev.max_score,
                percentage: latest.percentage ?? prev.percentage,
                decision: latest.decision || prev.decision,
                red_flags: latest.red_flags || prev.red_flags,
            }));
        } catch {
            /* silent — fresh scorecard if no existing one */
        }
    }, [id, type, isPrerec]);

    useEffect(() => {
        // RESET all form state when navigating to a different candidate or
        // scorecard type. Without this, leftover answers from a previously
        // viewed candidate would leak into the new candidate's form when
        // the new candidate has no scorecard yet (the "no-op" branch of
        // loadExistingScorecard wouldn't touch the state).
        setEntretienAnswers({});
        setRatings({});
        setGeneralComment("");
        setCandidate(null);
        setPreview({ total_score: 0, max_score: 0, percentage: 0, decision: "—", next_step: null, red_flags: [] });
        setSubmitResult(null);
        setLoading(true);
        Promise.all([loadCriteria(), loadCandidate(), loadScript(), loadExistingScorecard()])
            .finally(() => setLoading(false));
    }, [loadCriteria, loadCandidate, loadScript, loadExistingScorecard]);

    const requestPreview = useCallback(async (ratingArr) => {
        if (ratingArr.length === 0) {
            setPreview({ total_score: 0, max_score: 0, percentage: 0, decision: "—", next_step: null, red_flags: [] });
            return;
        }
        try {
            const { data } = await api.post("/scorecards/preview", {
                type,
                ratings: ratingArr,
            });
            setPreview(data);
        } catch {
            /* silent */
        }
    }, [type]);

    // --- Prerec — unified script handlers ---------------------------------
    const updateEntretien = (itemId, partial) => {
        setEntretienAnswers((prev) => {
            const cur = prev[itemId] || {};
            const nextItemState = { ...cur, ...partial };
            // Clean keys explicitly set to undefined/null
            Object.keys(partial).forEach((k) => {
                if (partial[k] === undefined || partial[k] === null) delete nextItemState[k];
            });
            const next = { ...prev, [itemId]: nextItemState };
            if (Object.prototype.hasOwnProperty.call(partial, "note")) {
                requestPreview(buildPrerecRatings(next, scriptItems));
            }
            return next;
        });
    };

    // --- Immersion — legacy blocks handlers -------------------------------
    const updateImmersionPreview = useCallback(async (next) => {
        const ratingArr = Object.entries(next)
            .filter(([, v]) => v.note)
            .map(([criterion_id, v]) => ({
                criterion_id,
                note: v.note,
                comment: v.comment || null,
            }));
        requestPreview(ratingArr);
    }, [requestPreview]);

    const onNoteChange = (criterion_id, note) => {
        setRatings((prev) => {
            const next = { ...prev, [criterion_id]: { ...prev[criterion_id], note } };
            if (note === undefined) delete next[criterion_id].note;
            updateImmersionPreview(next);
            return next;
        });
    };

    const onCommentChange = (criterion_id, comment) => {
        setRatings((prev) => ({ ...prev, [criterion_id]: { ...prev[criterion_id], comment } }));
    };

    // --- Submit -----------------------------------------------------------
    // The actual submit is split in two so we can interject a custom
    // confirmation modal when the form is partially filled. `submit()`
    // performs validation and EITHER stashes the payload in
    // `incompleteConfirm` (modal shown, user can review and confirm)
    // OR calls `_postScorecard()` directly when everything is filled.
    const submit = async () => {
        let ratingArr;
        let intake_answers = null;
        let answered = 0;
        let total = 0;

        if (isPrerec) {
            ratingArr = buildPrerecRatings(entretienAnswers, scriptItems);
            const ratableItems = scriptItems.filter((it) => it.criterion_ids?.length);
            const answeredItems = ratableItems.filter((it) => entretienAnswers[it.id]?.note);
            answered = answeredItems.length;
            total = ratableItems.length;
            intake_answers = entretienAnswers;
        } else {
            ratingArr = Object.entries(ratings)
                .filter(([, v]) => v.note)
                .map(([criterion_id, v]) => ({
                    criterion_id,
                    note: v.note,
                    comment: v.comment || null,
                }));
            answered = ratingArr.length;
            total = criteria.length;
        }

        const payload = { ratingArr, intake_answers };

        if (answered < total) {
            // Partial scorecard → ask for explicit confirmation before posting.
            setIncompleteConfirm({ answered, total, payload });
            return;
        }
        await _postScorecard(payload);
    };

    const _postScorecard = async ({ ratingArr, intake_answers }) => {
        setIncompleteConfirm(null);
        setSubmitting(true);
        try {
            const { data } = await api.post(`/scorecards/${id}`, {
                type,
                ratings: ratingArr,
                general_comment: generalComment || null,
                decision: preview.decision,
                intake_answers,
            });
            toast.success("Scorecard enregistrée");
            setSubmitResult({
                decision: data.decision || preview.decision,
                next_step: data.next_step || preview.next_step,
                percentage: data.candidate_updates?.[isPrerec ? "prerec_percent" : "immersion_percent"] || preview.percentage,
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setSubmitting(false);
        }
    };

    async function scheduleImmersion() {
        // Open the planning modal directly. The modal itself will PATCH the
        // candidate (set status + scheduled_at + referent + location) and
        // dismiss the post-submit overlay on success.
        setSubmitResult(null);            // dismiss post-submit overlay
        setShowImmersionModal(true);      // open the planning modal right away
    }

    async function rejectCandidate(reasonOverride, noteOverride) {
        // iter97 · Si aucun motif fourni, ouvre le picker pour forcer un
        // motif détaillé (non-régression : les 3 autres flows Scorecard
        // - handleUnreachable, handleArchiveUnavailable,
        //   handleRejectDefinitive - passent déjà leur propre `reason`).
        if (!reasonOverride) {
            setRejectReasonModal({ defaultReason: "" });
            return;
        }
        try {
            await api.post(`/candidates/${id}/reject`, {
                reason: reasonOverride,
                note: noteOverride || null,
            });
            // iter96 · Retirer le candidat refusé de la file d'appel locale
            // — sinon prev/next continue de pointer vers lui.
            removeFromQueue(id);
            toast.success("Candidat refusé");
            navigate(`/candidats/${id}`);
        } catch {
            toast.error("Erreur");
        }
    }

    // Referent / admin post-immersion primary CTA:
    // 1) validates the candidate  (status → "validated")
    // 2) triggers the hiring fiche email + magic link
    // 3) redirects to the Embauche board so RH sees the new "form_sent" card
    async function validateAndSendFiche() {
        setValidatingHiring(true);
        try {
            await api.post(`/candidates/${id}/validate`);
            try {
                await api.post(`/onboarding/candidates/${id}/send`);
                toast.success("Recrutement validé · fiche d'embauche envoyée au candidat");
            } catch (e) {
                // Validate worked but send-fiche failed — still show partial success
                toast.warning(
                    e.response?.data?.detail ||
                    "Candidat validé, mais l'envoi de la fiche a échoué. Réessayez depuis la fiche candidat.",
                );
            }
            navigate("/embauche");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de valider le recrutement");
        } finally {
            setValidatingHiring(false);
        }
    }

    async function markNoAnswer() {
        // Open the custom modal (replaces ugly window.confirm)
        setCallModalOpen(true);
    }

    function goToCandidate(targetId) {
        if (!targetId) return;
        navigate(`/candidats/${targetId}/scorecard/prerec`);
    }

    async function handleNoAnswerConfirmed() {
        setCallModalOpen(false);
        setMarkingNoAnswer(true);
        try {
            const { data } = await api.post(`/candidates/${id}/no-answer`, undefined, {
                offlineQueueable: true,
                offlineLabel: `Pas de réponse · ${candidate?.first_name || ""} ${candidate?.last_name || ""}`.trim(),
                optimisticData: { phone: candidate?.phone, call_attempts: (candidate?.call_attempts || 0) + 1, queuedOffline: true },
            });
            // Auto-suggest sending the "Pas de réponse" SMS to the candidate.
            // When the user closes the messaging modal, we then navigate to the next candidate.
            if (data?.phone) {
                toast.success(`« À rappeler » · Tentative ${data?.call_attempts || "+1"}`);
                setMessagingState({ category: "no_answer", advanceOnClose: true });
            } else {
                toast.success(
                    `« À rappeler » · Tentative ${data?.call_attempts || "+1"}`,
                    {
                        description: "Vous pouvez annuler cette action.",
                        action: {
                            label: "Annuler",
                            onClick: async () => {
                                try {
                                    await api.post(`/candidates/${id}/undo-no-answer`);
                                    toast.success("Pas de réponse annulé");
                                    navigate(`/candidats/${id}`);
                                } catch {
                                    toast.error("Annulation impossible");
                                }
                            },
                        },
                        duration: 8000,
                    },
                );
                if (queueInfo?.nextId) {
                    goToCandidate(queueInfo.nextId);
                } else if (queueInfo?.return_url) {
                    navigate(queueInfo.return_url);
                } else {
                    navigate(`/candidats?status=&to_recall=1`);
                }
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setMarkingNoAnswer(false);
        }
    }

    async function handleUnreachable() {
        setCallModalOpen(false);
        try {
            await api.post(`/candidates/${id}/reject`, { reason: "numero_injoignable" }, {
                offlineQueueable: true,
                offlineLabel: `Numéro injoignable · ${candidate?.first_name || ""} ${candidate?.last_name || ""}`.trim(),
            });
            removeFromQueue(id);  // iter96 · sort de la file d'appel locale
            toast.success("Numéro injoignable · candidat refusé", {
                description: "Sortie de la file d'appels.",
                duration: 5000,
            });
            if (queueInfo?.nextId) {
                goToCandidate(queueInfo.nextId);
            } else if (queueInfo?.return_url) {
                navigate(queueInfo.return_url);
            } else {
                navigate(`/candidats`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    async function handleArchiveUnavailable() {
        setCallModalOpen(false);
        try {
            await api.post(`/candidates/${id}/reject`, { reason: "candidat_indisponible" }, {
                offlineQueueable: true,
                offlineLabel: `Candidat plus disponible · ${candidate?.first_name || ""} ${candidate?.last_name || ""}`.trim(),
            });
            removeFromQueue(id);  // iter96 · sort de la file d'appel locale
            toast.success("Candidat archivé · plus disponible", {
                description: "Sa fiche reste consultable, hors file d'appels.",
                duration: 5000,
            });
            if (queueInfo?.nextId) {
                goToCandidate(queueInfo.nextId);
            } else if (queueInfo?.return_url) {
                navigate(queueInfo.return_url);
            } else {
                navigate(`/candidats`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    async function handleRejectDefinitive() {
        setCallModalOpen(false);
        try {
            await api.post(`/candidates/${id}/reject`, { reason: "criteres_non_remplis" }, {
                offlineQueueable: true,
                offlineLabel: `Refus définitif · ${candidate?.first_name || ""} ${candidate?.last_name || ""}`.trim(),
            });
            removeFromQueue(id);  // iter96 · sort de la file d'appel locale
            toast.success("Refus définitif · critères non remplis", {
                description: "Le candidat sort de la file d'appels.",
                duration: 5000,
            });
            if (queueInfo?.nextId) {
                goToCandidate(queueInfo.nextId);
            } else if (queueInfo?.return_url) {
                navigate(queueInfo.return_url);
            } else {
                navigate(`/candidats`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    function handleScheduleFromCallModal() {
        // Close the qualification modal first, then open the immersion scheduler
        setCallModalOpen(false);
        setShowImmersionModal(true);
    }

    const immersionBlocks = useMemo(() => {
        if (isPrerec) return [];
        const map = new Map();
        criteria.forEach((c) => {
            if (!map.has(c.block)) map.set(c.block, []);
            map.get(c.block).push(c);
        });
        return Array.from(map.entries());
    }, [criteria, isPrerec]);

    // Group entretien script by phase, preserving global numbering Q1..QN
    const phasedScript = useMemo(() => {
        if (!isPrerec) return [];
        const groups = new Map();
        scriptItems.forEach((item, idx) => {
            if (!groups.has(item.phase)) groups.set(item.phase, []);
            groups.get(item.phase).push({ item, number: idx + 1 });
        });
        return Array.from(groups.entries());
    }, [isPrerec, scriptItems]);

    if (loading || !candidate) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-shariti-orange" />
            </div>
        );
    }

    if (accessDenied) {
        return (
            <div className="card-soft p-10 text-center max-w-lg mx-auto mt-12" data-testid="scorecard-access-denied">
                <div className="h-14 w-14 rounded-2xl bg-red-50 text-shariti-red mx-auto inline-flex items-center justify-center">
                    <Lock className="h-7 w-7" />
                </div>
                <h2 className="mt-4 text-2xl font-black tracking-tight text-shariti-black">
                    Hors de votre périmètre
                </h2>
                <p className="mt-2 text-sm font-medium text-slate-500">
                    {isReferent
                        ? "La scorecard de pré-recrutement téléphonique est réservée à l'équipe pré-rec. Vous évaluez les candidats lors de leur immersion terrain."
                        : "La scorecard d'immersion est réservée aux référents terrain."}
                </p>
                <button
                    onClick={() => goBack(navigate, `/candidats/${id}`)}
                    className="mt-6 btn-shariti"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Retour
                </button>
            </div>
        );
    }

    const livePercentage = preview.percentage || 0;
    const phoneE164 = toE164(candidate.phone);
    const waLink = phoneE164
        ? `https://wa.me/${phoneE164.replace("+", "")}?text=${encodeURIComponent(`Bonjour ${candidate.first_name}, je suis de Shariti à propos de votre candidature.`)}`
        : null;
    const priorityQuestions = candidate.ai_analysis?.priority_questions || [];

    return (
        <div className="space-y-6 pb-24 md:pb-8" data-testid={`scorecard-${type}`}>
            {/* === UNIFIED STICKY CALL BAR === */}
            {/* All-in-one : queue navigation + candidate identity + tags (offre / pépite / risque) + actions + CV */}
            <div
                className="sticky top-14 md:top-16 z-20 -mt-4 md:-mt-6 mb-4 mx-[-1rem] md:mx-[-2rem] bg-shariti-cream border-b-2 border-shariti-gray shadow-md"
                data-testid="scorecard-sticky-bar"
            >
                {/* Row 1 — Queue navigation (only if a queue exists) */}
                {isPrerec && queueInfo && (
                    <div
                        className="px-4 md:px-8 py-1.5 md:py-2 flex flex-wrap items-center gap-2 md:gap-3 bg-shariti-orange-soft/40 border-b border-shariti-orange/15"
                        data-testid="queue-banner"
                    >
                        <ListChecks className="h-4 w-4 text-shariti-orange shrink-0" />
                        <div className="hidden md:block text-[10px] font-bold uppercase tracking-widest text-shariti-orange truncate">
                            File d'appels · {queueInfo.label}
                        </div>
                        <div className="text-[11px] md:text-xs font-extrabold text-shariti-black">
                            {queueInfo.position} / {queueInfo.total}
                        </div>
                        <div className="ml-auto flex items-center gap-1.5 md:gap-2">
                            <button
                                onClick={() => goToCandidate(queueInfo.prevId)}
                                disabled={!queueInfo.prevId}
                                data-testid="queue-prev"
                                title="Candidat précédent"
                                className="h-7 md:h-8 px-2 md:px-3 inline-flex items-center gap-1 rounded-full bg-white border border-shariti-orange/30 text-shariti-orange hover:bg-shariti-orange hover:text-white transition disabled:opacity-30 disabled:cursor-not-allowed text-[11px] font-extrabold"
                            >
                                <ChevronLeft className="h-3.5 w-3.5" />
                                <span className="hidden md:inline">Précédent</span>
                            </button>
                            <button
                                onClick={() => goToCandidate(queueInfo.nextId)}
                                disabled={!queueInfo.nextId}
                                data-testid="queue-next"
                                title="Candidat suivant"
                                className="h-7 md:h-8 px-2 md:px-3 inline-flex items-center gap-1 rounded-full bg-shariti-orange text-white hover:bg-[#d66528] transition disabled:opacity-30 disabled:cursor-not-allowed text-[11px] font-extrabold shadow-btn"
                            >
                                <span className="hidden md:inline">Suivant</span>
                                <ChevronRight className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Row 2 — Identity + tags + actions
                    Layout: on mobile we put identity on row 1 and the action
                    cluster (phone / CV / message) on row 2 so the candidate
                    NAME stays readable (used to get clobbered by the giant
                    +33 phone number button). On md+ everything fits on one row. */}
                <div className="px-4 md:px-8 py-2 md:py-3 flex flex-col md:flex-row md:flex-wrap md:items-center gap-2 md:gap-4">
                    {/* Identity row — back + avatar + name + badges */}
                    <div className="flex items-center gap-2 md:gap-4 min-w-0 md:flex-1">
                        <button
                            onClick={() => goBack(navigate)}
                            data-testid="scorecard-back"
                            className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white border border-shariti-gray text-slate-500 hover:text-shariti-orange hover:border-shariti-orange transition shrink-0"
                            title="Retour"
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </button>
                        {candidate.avatar_url ? (
                            <img src={candidate.avatar_url} alt="" className="h-9 w-9 md:h-10 md:w-10 rounded-xl object-cover shrink-0" />
                        ) : (
                            <div className="h-9 w-9 md:h-10 md:w-10 rounded-xl bg-shariti-orange-soft text-shariti-orange font-extrabold text-sm flex items-center justify-center shrink-0">
                                {candidate.first_name?.[0]}
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <div className="text-sm md:text-base font-extrabold text-shariti-black truncate min-w-0">
                                    {candidate.first_name} {candidate.last_name}
                                </div>
                                {/* Pépite badge */}
                                {(candidate.ai_score || 0) >= 82 && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-shariti-orange text-white shrink-0" title={`Score IA ${candidate.ai_score}/100`}>
                                        <Sparkles className="h-2.5 w-2.5" /> Pépite
                                    </span>
                                )}
                                {/* Risk badge */}
                                {candidate.risk_level === "high" && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-50 text-shariti-red border border-red-200 shrink-0" title="Risque élevé">
                                        <AlertTriangle className="h-2.5 w-2.5" /> Risque
                                    </span>
                                )}
                                {/* AI score number badge — neutral context */}
                                {candidate.ai_score > 0 && (
                                    <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-shariti-cream text-slate-600 border border-shariti-gray shrink-0" title="Score IA initial">
                                        IA {candidate.ai_score}
                                    </span>
                                )}
                            </div>
                            <div className="mt-0.5 hidden md:flex items-center gap-2 flex-wrap text-[11px] font-semibold text-slate-500 min-w-0">
                                {candidate.layan_offer_title && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest text-shariti-orange truncate max-w-[260px]" title={`Origine : ${candidate.layan_offer_title}`}>
                                        <ClipboardCheck className="h-2.5 w-2.5 shrink-0" />
                                        {candidate.layan_offer_title}
                                    </span>
                                )}
                                {candidate.city && <span>· {candidate.city}</span>}
                                {candidate.profile_dominant && <span>· {candidate.profile_dominant.replace(/_/g, " ")}</span>}
                            </div>
                        </div>
                    </div>
                    {/* Action cluster — on mobile this becomes row 2 (under
                        the identity). On md+ it stays on the same row.
                        Phone button: on mobile we render an icon-only
                        compact button so it stops eating the candidate's name. */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <a
                            href={`tel:${phoneE164}`}
                            data-testid="scorecard-call-button"
                            title={candidate.phone || "Appeler"}
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-shariti-orange text-white h-9 md:h-10 w-9 md:w-auto md:px-4 font-bold text-xs md:text-sm shadow-btn hover:bg-[#d66528] transition active:scale-95 whitespace-nowrap shrink-0"
                        >
                            <Phone className="h-4 w-4 shrink-0" />
                            <span className="hidden md:inline whitespace-nowrap">
                                Composer&nbsp;{candidate.phone}
                            </span>
                        </a>
                        {isPrerec && (
                            <button
                                onClick={markNoAnswer}
                                disabled={markingNoAnswer}
                                data-testid="scorecard-no-answer"
                                title="Pas de réponse — à rappeler"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-orange hover:border-shariti-orange h-9 md:h-10 w-9 md:w-auto md:px-3 font-bold text-xs transition shrink-0"
                            >
                                {markingNoAnswer ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneOff className="h-4 w-4" />}
                                <span className="hidden md:inline">Qualifier</span>
                            </button>
                        )}
                        {candidate.cv_url && (
                            <a
                                href={candidate.cv_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="scorecard-download-cv"
                                title="Télécharger le CV (PDF)"
                                className="h-9 md:h-10 w-9 md:w-auto md:px-3 inline-flex items-center justify-center gap-1.5 rounded-full bg-shariti-black text-white hover:bg-slate-800 font-extrabold text-[11px] transition shadow-sm shrink-0"
                            >
                                <FileDown className="h-4 w-4" />
                                <span className="hidden md:inline">CV</span>
                            </a>
                        )}
                        {candidate.phone && (
                            <button
                                type="button"
                                onClick={() => setMessagingState({ category: "custom" })}
                                data-testid="scorecard-send-message"
                                title="Envoyer SMS ou WhatsApp avec modèle"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white h-9 md:h-10 w-9 md:w-auto md:px-3 font-bold text-xs md:text-sm transition shadow-btn shrink-0"
                            >
                                <MessageCircle className="h-4 w-4 shrink-0" />
                                <span className="hidden md:inline">Message</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* AI priority questions panel - displayed BEFORE scorecard so recruiter can scan them */}
            {priorityQuestions.length > 0 && isPrerec && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="card-soft p-5 md:p-6 bg-shariti-orange-soft/60 border-shariti-orange/30"
                    data-testid="ai-priority-questions"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="h-4 w-4 text-shariti-orange" />
                        <h3 className="text-sm font-extrabold uppercase tracking-widest text-shariti-orange">
                            ✨ IA · Points d'attention détectés
                        </h3>
                    </div>
                    <ul className="space-y-2 text-sm font-medium text-shariti-black">
                        {priorityQuestions.slice(0, 5).map((q, i) => (
                            <li key={i} className="flex gap-2">
                                <span className="font-black text-shariti-orange">·</span>
                                <span>{q}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        Garder ces points à l'esprit pendant l'entretien — la trame ci-dessous reste votre fil conducteur.
                    </div>
                </motion.div>
            )}

            <div className="grid lg:grid-cols-[1fr_360px] gap-6">
                {/* Form */}
                <div className="space-y-5">
                    <div className="card-soft p-6 md:p-8">
                        <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                            <ClipboardCheck className="h-3 w-3" /> {meta.accent}
                        </div>
                        <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">{meta.title}</h1>
                        <p className="mt-2 text-sm font-medium text-slate-500">{meta.subtitle}</p>
                        <div className="mt-4 flex items-center gap-3">
                            {candidate.avatar_url ? (
                                <img src={candidate.avatar_url} alt="" className="h-10 w-10 rounded-xl object-cover" />
                            ) : (
                                <div className="h-10 w-10 rounded-xl bg-shariti-orange-soft text-shariti-orange font-extrabold flex items-center justify-center">
                                    {candidate.first_name?.[0]}
                                </div>
                            )}
                            <div>
                                <div className="text-sm font-extrabold text-shariti-black">{candidate.first_name} {candidate.last_name}</div>
                                <div className="text-xs font-medium text-slate-500">{candidate.city} · {candidate.position}</div>
                            </div>
                        </div>
                        {isPrerec && (
                            <div className="mt-5 p-3 rounded-2xl bg-shariti-cream border border-shariti-gray text-[11px] font-semibold text-slate-600 leading-relaxed">
                                <span className="font-extrabold text-shariti-black">Fil conducteur unique</span> · Lisez chaque question à voix haute, dans l'ordre. Les questions notables alimentent le score live à droite. Les questions <span className="font-bold text-amber-700">éliminatoires</span> stoppent l'entretien si le candidat ne convient pas.
                            </div>
                        )}
                    </div>

                    {isPrerec ? (
                        // --- Unified script: Q1 → Q17 grouped by phase ---
                        phasedScript.map(([phaseKey, items]) => {
                            const phase = PHASES[phaseKey];
                            return (
                                <div key={phaseKey} className="card-soft p-5 md:p-7" data-testid={`phase-${phaseKey}`}>
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="h-8 w-8 rounded-xl bg-shariti-orange text-white font-black flex items-center justify-center text-sm">
                                            {phase.emoji_safe}
                                        </div>
                                        <h3 className="text-sm md:text-base font-black uppercase tracking-widest text-shariti-orange">
                                            {phase.label}
                                        </h3>
                                    </div>
                                    <div className="space-y-3">
                                        {items.map(({ item, number }) => {
                                            const state = entretienAnswers[item.id] || {};
                                            const gatingFail =
                                                (item.kind === "yesno_gating" && state.answer === "no") ||
                                                (item.kind === "rating_gating" && state.note && state.note <= 2);
                                            return (
                                                <EntretienItem
                                                    key={item.id}
                                                    item={item}
                                                    number={number}
                                                    state={state}
                                                    onUpdate={(partial) => updateEntretien(item.id, partial)}
                                                    gatingFail={gatingFail}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        // --- Immersion: legacy critères blocks ---
                        immersionBlocks.map(([blockName, items]) => (
                            <div key={blockName} className="card-soft p-6 md:p-8" data-testid={`block-${blockName}`}>
                                <h3 className="text-sm font-extrabold uppercase tracking-widest text-shariti-orange">{blockName}</h3>
                                <div className="mt-4 divide-y divide-shariti-gray">
                                    {items.map((c) => {
                                        const cur = ratings[c.id] || {};
                                        return (
                                            <motion.div
                                                key={c.id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="py-4 first:pt-0 last:pb-0"
                                                data-testid={`criterion-${c.id}`}
                                            >
                                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-base font-extrabold text-shariti-black">{c.label}</span>
                                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-shariti-cream text-slate-500 border border-shariti-gray">
                                                                ×{c.weight}
                                                            </span>
                                                        </div>
                                                        <div className="mt-1 text-xs font-medium text-slate-500 flex items-start gap-1.5">
                                                            <Lightbulb className="h-3.5 w-3.5 text-shariti-orange mt-0.5 shrink-0" />
                                                            <span>{c.question}</span>
                                                        </div>
                                                    </div>
                                                    <NoteSelector value={cur.note} onChange={(n) => onNoteChange(c.id, n)} />
                                                </div>
                                                <input
                                                    type="text"
                                                    value={cur.comment || ""}
                                                    onChange={(e) => onCommentChange(c.id, e.target.value)}
                                                    placeholder="Commentaire (optionnel)"
                                                    data-testid={`comment-${c.id}`}
                                                    className="mt-3 w-full bg-shariti-cream border border-shariti-gray rounded-xl px-3 py-2 text-xs font-medium text-shariti-black outline-none focus:border-shariti-orange focus:ring-2 focus:ring-shariti-orange/10"
                                                />
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}

                    <div className="card-soft p-6 md:p-8">
                        <h3 className="text-sm font-extrabold uppercase tracking-widest text-slate-500">Commentaire général</h3>
                        <textarea
                            value={generalComment}
                            onChange={(e) => setGeneralComment(e.target.value)}
                            rows={4}
                            data-testid="general-comment"
                            placeholder="Impression d'ensemble, points marquants, prochaine étape suggérée…"
                            className="mt-3 w-full bg-shariti-cream border border-shariti-gray rounded-xl px-4 py-3 text-sm font-medium text-shariti-black outline-none focus:border-shariti-orange focus:ring-4 focus:ring-shariti-orange/10 resize-none"
                        />
                    </div>

                    <button
                        onClick={submit}
                        disabled={submitting}
                        data-testid="scorecard-submit"
                        className="btn-shariti w-full md:w-auto"
                    >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Enregistrer la scorecard
                    </button>
                </div>

                {/* Live preview panel */}
                <aside className="lg:sticky lg:top-32 self-start space-y-4" data-testid="live-preview">
                    <motion.div
                        layout
                        className="card-soft p-6 text-center"
                    >
                        <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                            <Sparkles className="h-3 w-3" /> Score live
                        </div>
                        <div className="mt-4 flex justify-center">
                            <ScoreRing score={livePercentage} size={140} stroke={9} label="" />
                        </div>
                        <div className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-400">
                            {preview.total_score?.toFixed?.(1) ?? 0} / {preview.max_score?.toFixed?.(1) ?? 0}
                        </div>
                        <div className={`mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-extrabold ${
                            livePercentage >= 85 ? "bg-emerald-50 text-emerald-700"
                            : livePercentage >= 70 ? "bg-shariti-orange-soft text-shariti-orange"
                            : livePercentage >= 55 ? "bg-amber-50 text-amber-700"
                            : "bg-red-50 text-shariti-red"
                        }`}>
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {preview.decision}
                        </div>
                        {preview.next_step && (
                            <div className="mt-2 text-[11px] font-semibold text-slate-500">{preview.next_step}</div>
                        )}
                    </motion.div>

                    {preview.red_flags?.length > 0 && (
                        <div className="card-soft p-5 bg-red-50/40 border-red-200/60">
                            <div className="flex items-center gap-2 text-shariti-red font-extrabold text-xs uppercase tracking-widest">
                                <AlertTriangle className="h-4 w-4" /> Red flags
                            </div>
                            <ul className="mt-3 space-y-1.5 text-xs font-semibold text-shariti-red">
                                {preview.red_flags.map((rf, i) => <li key={i}>· {rf}</li>)}
                            </ul>
                        </div>
                    )}

                    <div className="card-soft p-5">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Légende notes</div>
                        <ul className="text-xs font-semibold text-slate-600 space-y-1">
                            <li>· 1-2 : Faible — red flag</li>
                            <li>· 3 : Moyen — à surveiller</li>
                            <li>· 4-5 : Fort — point d'appui</li>
                        </ul>
                    </div>
                </aside>
            </div>

            {/* Post-submit modal — what's next? */}
            {submitResult && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 z-50 bg-shariti-black/40 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
                    data-testid="post-submit-modal"
                    onClick={() => navigate(`/candidats/${id}`)}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-3xl shadow-2xl p-7 md:p-10 max-w-lg w-full my-auto max-h-[calc(100dvh-2rem)] overflow-y-auto"
                    >
                        <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-emerald-100 text-emerald-600 mx-auto">
                            <CheckCircle2 className="h-7 w-7" strokeWidth={2.4} />
                        </div>
                        <h2 className="mt-4 text-2xl font-black tracking-tight text-shariti-black text-center">
                            {isPrerec ? "Pré-qualification enregistrée" : "Immersion enregistrée"}
                        </h2>
                        <p className="mt-2 text-sm font-medium text-slate-500 text-center">
                            Score {submitResult.percentage}% · {submitResult.decision}
                        </p>
                        <div className="mt-6 p-4 rounded-2xl bg-shariti-orange-soft border border-shariti-orange/20">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-shariti-orange mb-1">
                                Prochaine étape recommandée
                            </div>
                            <div className="text-sm font-extrabold text-shariti-black">
                                {isPrerec
                                    ? (submitResult.next_step || "Voir la fiche pour décider")
                                    : (submitResult.percentage >= 70
                                        ? "Valider le recrutement et déclencher la fiche d'embauche"
                                        : "Décider (valider ou refuser) sur la fiche du candidat")}
                            </div>
                        </div>
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-2">
                            {isPrerec ? (
                                <button
                                    onClick={scheduleImmersion}
                                    data-testid="modal-schedule-immersion"
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-shariti-orange text-white font-bold px-4 py-3 text-sm hover:bg-[#d66528] transition shadow-btn"
                                >
                                    <MapPin className="h-4 w-4" />
                                    Programmer immersion
                                </button>
                            ) : (
                                <button
                                    onClick={validateAndSendFiche}
                                    disabled={validatingHiring}
                                    data-testid="modal-validate-hiring"
                                    className="md:col-span-2 inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 text-white font-bold px-4 py-3 text-sm hover:bg-emerald-700 transition shadow-btn disabled:opacity-70"
                                >
                                    {validatingHiring ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
                                    Valider & envoyer la fiche d&apos;embauche
                                </button>
                            )}
                            {isPrerec && candidate?.phone && (
                                <button
                                    onClick={() => setMessagingState({ category: "recap_interview" })}
                                    data-testid="modal-send-recap"
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-500 text-white font-bold px-4 py-3 text-sm hover:bg-emerald-600 transition shadow-btn"
                                >
                                    <MessageCircle className="h-4 w-4" />
                                    Envoyer SMS récap
                                </button>
                            )}
                            <button
                                onClick={() => rejectCandidate()}
                                data-testid="modal-reject"
                                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white text-shariti-red border border-red-200 font-bold px-4 py-3 text-sm hover:bg-red-50 transition"
                            >
                                <UserX className="h-4 w-4" />
                                Refuser
                            </button>
                            {queueInfo?.nextId ? (
                                <button
                                    onClick={() => goToCandidate(queueInfo.nextId)}
                                    data-testid="modal-next-candidate"
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-shariti-black text-white font-bold px-4 py-3 text-sm hover:bg-slate-800 transition shadow-btn"
                                >
                                    Candidat suivant
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            ) : (
                                <button
                                    onClick={() => navigate(`/candidats/${id}`)}
                                    data-testid="modal-later"
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white text-slate-600 border border-shariti-gray font-bold px-4 py-3 text-sm hover:border-shariti-orange hover:text-shariti-orange transition"
                                >
                                    Décider plus tard
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}

            {/* Call action modal — replaces window.confirm */}
            {callModalOpen && candidate && (
                <CallActionModal
                    candidate={candidate}
                    callAttempts={candidate.call_attempts || 0}
                    onSchedule={handleScheduleFromCallModal}
                    onNoAnswer={handleNoAnswerConfirmed}
                    onArchiveUnavailable={handleArchiveUnavailable}
                    onRejectDefinitive={handleRejectDefinitive}
                    onUnreachable={handleUnreachable}
                    onTalentPool={() => { setCallModalOpen(false); setTalentPoolModalOpen(true); }}
                    onClose={() => setCallModalOpen(false)}
                />
            )}

            {/* Talent pool modal — park candidate from the Qualifier flow */}
            {talentPoolModalOpen && candidate && (
                <TalentPoolModal
                    candidate={candidate}
                    defaultReason={(candidate.call_attempts || 0) >= 2 ? "cant_reach" : "to_recontact_later"}
                    onClose={() => setTalentPoolModalOpen(false)}
                    onParked={() => {
                        setTalentPoolModalOpen(false);
                        // Advance to next candidate in queue (the parked one is no longer in active queue)
                        if (queueInfo?.nextId) {
                            goToCandidate(queueInfo.nextId);
                        } else if (queueInfo?.return_url) {
                            navigate(queueInfo.return_url);
                        } else {
                            navigate("/candidats");
                        }
                    }}
                />
            )}

            {/* Schedule immersion modal — opened from the qualification modal */}
            {showImmersionModal && candidate && (
                <ScheduleImmersionModal
                    candidate={candidate}
                    onCancel={() => setShowImmersionModal(false)}
                    onSuccess={() => {
                        setShowImmersionModal(false);
                        toast.success("Immersion programmée ✅", {
                            description: "Envoyez le SMS récap avec le lien d'engagement.",
                        });
                        // Open the recap message modal right away — the candidate now has a token
                        setMessagingState({ category: "recap_interview", advanceOnClose: true });
                    }}
                />
            )}

            {/* iter97 · Force reject reason picker for generic "Refuser" clicks */}
            {rejectReasonModal && candidate && (
                <RejectReasonModal
                    open={true}
                    onOpenChange={(v) => { if (!v) setRejectReasonModal(null); }}
                    candidate={candidate}
                    defaultReason={rejectReasonModal.defaultReason}
                    onConfirm={async (reason, note) => {
                        setRejectReasonModal(null);
                        await rejectCandidate(reason, note);
                    }}
                />
            )}

            {/* SMS / WhatsApp messaging modal */}
            {messagingState && candidate && (
                <SendMessageModal
                    candidate={candidate}
                    defaultCategory={messagingState.category}
                    onClose={() => {
                        const shouldAdvance = messagingState.advanceOnClose;
                        setMessagingState(null);
                        // After "Pas de réponse" flow: advance to next candidate in queue when modal closes
                        if (shouldAdvance) {
                            if (queueInfo?.nextId) {
                                goToCandidate(queueInfo.nextId);
                            } else if (queueInfo?.return_url) {
                                navigate(queueInfo.return_url);
                            } else {
                                navigate(`/candidats?status=&to_recall=1`);
                            }
                        }
                    }}
                />
            )}

            {/* Confirmation modal — replaces window.confirm for incomplete scorecards */}
            {incompleteConfirm && (
                <ConfirmModal
                    title={isPrerec ? "Scorecard partielle" : "Scorecard partielle"}
                    body={
                        <>
                            Vous avez noté <strong>{incompleteConfirm.answered}</strong>
                            {" / "}{incompleteConfirm.total}{" "}
                            {isPrerec ? "questions notables" : "critères"}. Êtes-vous
                            sûr·e de vouloir enregistrer la scorecard maintenant ?
                            Vous pourrez compléter plus tard depuis la fiche du candidat.
                        </>
                    }
                    confirmLabel="Enregistrer quand même"
                    cancelLabel="Continuer la saisie"
                    tone="orange"
                    icon={AlertTriangle}
                    onConfirm={() => _postScorecard(incompleteConfirm.payload)}
                    onCancel={() => setIncompleteConfirm(null)}
                    testid="scorecard-incomplete-confirm"
                />
            )}
        </div>
    );
}
